import { useState } from 'react';
import { ShieldCheck, UserRound } from 'lucide-react';
import { systemToolsService } from '../../services';
import { useAuthStore } from '../../stores';
import { toErrorMessage } from '../../stores/marketplaceStoreTypes';
import { useFloatingAction } from '../layout/floatingActions';

interface RoleSwitcherFabProps {
    enabled: boolean;
}

export function RoleSwitcherFab({ enabled }: RoleSwitcherFabProps) {
    const user = useAuthStore((state) => state.user);
    const [isSwitching, setIsSwitching] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const targetIsAdmin = user ? !user.is_admin : false;

    const handleSwitch = async () => {
        if (isSwitching) {
            return;
        }

        setIsSwitching(true);
        setError(null);

        try {
            const response = await systemToolsService.toggleRole();
            window.location.assign(response.data.user.is_admin ? '/admin' : '/');
        } catch (requestError) {
            setError(toErrorMessage(requestError, 'No se pudo cambiar el rol. Inténtalo nuevamente.'));
            setIsSwitching(false);
        }
    };

    const content = (
        <div className="flex max-w-full flex-col items-end gap-2">
            {error ? (
                <div
                    role="alert"
                    className="max-w-[min(22rem,calc(100vw-2rem))] break-words rounded-xl border border-danger/30 bg-surface px-4 py-3 text-sm text-danger shadow-lg"
                >
                    {error}
                </div>
            ) : null}
            <button
                type="button"
                onClick={() => void handleSwitch()}
                disabled={isSwitching}
                aria-label={targetIsAdmin ? 'Cambiar a administrador' : 'Cambiar a usuario normal'}
                className="flex min-h-12 max-w-full touch-manipulation items-center gap-2 rounded-full border border-border bg-surface px-3 py-3 text-sm font-semibold text-text shadow-lg transition-[border-color,background-color,transform] hover:border-primary/30 hover:bg-hover active:scale-[0.98] disabled:cursor-wait disabled:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 sm:px-4"
            >
                <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary" aria-hidden="true">
                    {targetIsAdmin ? <ShieldCheck className="size-4" /> : <UserRound className="size-4" />}
                </span>
                <span className="whitespace-nowrap">
                    {isSwitching ? 'Cambiando…' : targetIsAdmin ? 'Ser admin' : 'Ser usuario'}
                </span>
            </button>
        </div>
    );

    useFloatingAction({
        id: 'role-switcher',
        order: 20,
        visible: enabled && user !== null,
        content,
    });

    return null;
}
